/* @flow */
import React, {
  useContext, useMemo, useCallback, useEffect,
} from 'react';
import { useSelector } from 'react-redux';
import { useLazyQuery } from 'react-apollo';
import {
  StyleSheet, View, TouchableOpacity,
} from 'react-native';
import { Text } from 'react-native-paper';
import dayjs from 'dayjs';
import gql from 'graphql-tag';

import {
  nowViewSelector, fullDailyViewSelector,
} from '../modules/todo/selectors';
import l, { timeString } from '../utils/localization';
import ActionsContext from '../contexts/actions';
import { useColor } from '../contexts/theme';
import useToggleState from '../hooks/useToggleState';
import { useGetAllTodos } from '../modules/todo/actions';
import { tokenSelector, isLoggedInSelector } from '../modules/auth/selectors';
import { RefreshingProvider } from '../contexts/RefreshingContext';
import DraggableList from '../components/DraggableList';
import EmptyComponent from '../components/EmptyComponent';
import ListFooter from '../components/DraggableList/ListFooter';
import Overlay from '../components/Overlay';
import { OverlayContextProvider } from '../contexts/OverlayContext';


const styles = StyleSheet.create({
  congratulationText: { textAlign: 'center', fontSize: 20, fontWeight: 'bold' },
  toggleText: { fontSize: 16, fontWeight: 'bold', textAlign: 'center' },
  nextText: { textAlign: 'center' },
  expanderContainer: { padding: 20 },
});

const Expander = ({
  hasMoreStuff, nextCalendarEvent, moreStuff, toggleShowAll, showAll,
}) => {
  const color = useColor('text');

  return useMemo(() => {
    const text = nextCalendarEvent
      ? `${l.nextMeetingAt} ${dayjs(nextCalendarEvent.startDate).format(timeString)}`
      : `${moreStuff} ${l.thingsLaterToday}`;

    return hasMoreStuff ? (
      <TouchableOpacity onPress={toggleShowAll}>
        <View style={styles.expanderContainer}>
          { !showAll
            ? <Text style={[styles.nextText, { color }]}>{ text }</Text>
            : null }
          <Text style={[styles.toggleText, { color }]}>
            { showAll ? l.hideUpcoming : l.showEntireDay }
          </Text>
        </View>
      </TouchableOpacity>
    ) : null;
  }, [color, hasMoreStuff, moreStuff, nextCalendarEvent, showAll, toggleShowAll]);
};

const StatsQuery = gql`query StatsQuery($token:ID!, $from:DateTime!, $to:DateTime!) {
  me(token:$token){
    doneCountToday: countForPeriod(status:done, to: $to, from: $from)
    postponeCountToday: countForPeriod(status:postponed, to: $to, from: $from)
  }
}`;

const Stats = () => {
  const token = useSelector(tokenSelector),
        isLoggedIn = useSelector(isLoggedInSelector),
        isOutboxEmpty = useSelector((state) => state.offline.outbox.length === 0),
        [loadStats, { data }] = useLazyQuery(StatsQuery, {
          fetchPolicy: 'network-only',
          variables: {
            token,
            from: dayjs().startOf('day').toDate(),
            to: dayjs().endOf('day').toDate(),
          },
        });

  useEffect(() => {
    if (isLoggedIn && isOutboxEmpty) {
      loadStats();
    }
  }, [isLoggedIn, loadStats, isOutboxEmpty]);

  if (!data || !data.me) {
    return null;
  }

  const text = data.me.doneCountToday && data.me.postponeCountToday
    ? `Today you've completed ${data.me.doneCountToday} tasks and made plans for ${data.me.postponeCountToday} tasks!`
    : data.me.doneCountToday
      ? `Today you've completed ${data.me.doneCountToday} tasks!`
      : data.me.postponeCountToday
        ? `Today you've made plans for ${data.me.postponeCountToday} tasks!`
        : null;

  return text ? (
    <Text style={{ paddingTop: 10, textAlign: 'center' }}>{ text }</Text>
  ) : null;
};

const TodoListActive = (props: Object) => {
  const nowData = useSelector(nowViewSelector),
        allData = useSelector(fullDailyViewSelector),
        actions = useContext(ActionsContext),
        onRefresh = useGetAllTodos(),
        [showAll, toggleShowAll] = useToggleState(false),
        rightActions = useMemo(() => [actions.done, actions.delete], [actions]),
        leftActions = useMemo(() => [actions.postpone], [actions]),
        moreStuff = allData.length - nowData.length,
        nextCalendarEvent = useMemo(() => allData.find((i) => i.isCalendarEvent && i.startDate > new Date()), [allData]),
        hasMoreStuff = moreStuff > 0,
        hasMoreToShow = !showAll && nowData.length === 0,
        data = showAll ? allData : nowData,
        color = useColor('text'),
        showAnimation = data.length === 0;


  const ListFooterComponent = useCallback(() => hasMoreToShow ? null : (
    <>
      <Expander
        nextCalendarEvent={nextCalendarEvent}
        moreStuff={moreStuff}
        hasMoreStuff={hasMoreStuff}
        showAll={showAll}
        toggleShowAll={toggleShowAll}
      />
      <ListFooter />
    </>
  ), [hasMoreStuff, hasMoreToShow, moreStuff, nextCalendarEvent, showAll, toggleShowAll]);

  const ListEmptyComponent = useCallback(() => {
    const uniqueness = dayjs().date();

    return (
      <EmptyComponent uniqueness={uniqueness}>
        <Text style={[styles.congratulationText, {
          color,
        }]}
        >
          { l.allDoneGoodJob }
        </Text>
        <Stats />
        <Expander
          nextCalendarEvent={nextCalendarEvent}
          moreStuff={moreStuff}
          hasMoreStuff={hasMoreStuff}
          showAll={showAll}
          toggleShowAll={toggleShowAll}
        />
      </EmptyComponent>
    );
  }, [color, hasMoreStuff, moreStuff, nextCalendarEvent, showAll, toggleShowAll]);

  const onRefreshFn = useCallback(() => onRefresh(true), [onRefresh]);

  const manualSortOrder = data && data.length > 0
    ? data[0].manualSortOrder + 1
    : 0;

  const newTaskTemplate = useMemo(() => ({ manualSortOrder }), [manualSortOrder]);

  return useMemo(() => (
    <OverlayContextProvider newTaskTemplate={newTaskTemplate}>
      <RefreshingProvider onRefresh={onRefreshFn}>
        <DraggableList
          data={data}
          ListEmptyComponent={ListEmptyComponent}
          leftActions={leftActions}
          rightActions={rightActions}
          ListFooterComponent={ListFooterComponent}
          {...props}
        />
        <Overlay showAnimation={showAnimation} />
      </RefreshingProvider>
    </OverlayContextProvider>
  ), [ListEmptyComponent, ListFooterComponent, data, leftActions, newTaskTemplate, onRefreshFn, props, rightActions, showAnimation]);
};

export default TodoListActive;
